import {useLayoutEffect, useRef, useState} from 'react'

/**
 * Measure the distance in pixels from the given element's outer-left/right
 * edges to the viewport's left/right edges, so a child can break out to the
 * viewport via `margin-left: -leftPx`, `margin-right: -rightPx`. Re-measures
 * on resize.
 *
 * Using measured pixel values (not `calc(50% - 50vw)`) avoids miscalcs from
 * vertical scrollbars, ancestor padding, and rounded layouts. Returns
 * `{leftPx, rightPx}` (zero when SSR / not yet measured).
 *
 * @param {unknown} refreshKey - Changes when async carousel content appears.
 * @returns {{ref: React.RefObject<HTMLElement>, leftPx: number, rightPx: number}}
 */
export const useViewportBleed = (refreshKey) => {
    const ref = useRef(null)
    const [bleed, setBleed] = useState({leftPx: 0, rightPx: 0})

    useLayoutEffect(() => {
        const node = ref.current
        if (!node || typeof window === 'undefined') return undefined

        const measure = () => {
            const rect = node.getBoundingClientRect()
            /* `documentElement.clientWidth` excludes the vertical scrollbar
             * (unlike `window.innerWidth`), matching what `100%` resolves
             * to for a body-width-stretched ancestor. Using it for the
             * right edge keeps the carousel's right boundary aligned with
             * the visible viewport edge rather than overshooting under the
             * scrollbar gutter. */
            const viewportWidth = document.documentElement.clientWidth
            const leftPx = Math.max(0, Math.round(rect.left))
            const rightPx = Math.max(0, Math.round(viewportWidth - rect.right))
            setBleed((prev) =>
                prev.leftPx === leftPx && prev.rightPx === rightPx ? prev : {leftPx, rightPx}
            )
        }

        measure()
        window.addEventListener('resize', measure)
        const ro =
            typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
        if (ro) ro.observe(node)
        /* Re-measure once fonts/images settle in case layout shifts after
         * the initial paint. */
        const raf = requestAnimationFrame(measure)

        return () => {
            window.removeEventListener('resize', measure)
            if (ro) ro.disconnect()
            cancelAnimationFrame(raf)
        }
    }, [refreshKey])

    return {ref, ...bleed}
}
